import Vue from 'vue';
import ToastUtils from '@/utils/toast';

/**
 * So sánh giờ máy (Date.now()) với mốc UTC từ Internet.
 * Header `Date` của API chỉ dùng để cảnh báo khi chênh lớn — không dùng để kết luận "đồng hồ đúng"
 * (tránh máy và server cùng sai giờ mà vẫn bỏ qua kiểm tra).
 */
export const CLOCK_SKEW_WARN_THRESHOLD_MS = 5 * 60 * 1000;

const SESSION_WARNED_KEY = 'pvs_user_clock_warned_v1';
const FETCH_MS = 8000;
const PUBLIC_CHECK_RETRY_MS = 15000;
const MAX_PUBLIC_CHECK_ATTEMPTS = 6;

/** Đã xong bước kiểm tra chính (mốc Internet) */
let publicCheckFinished = false;
let publicCheckInFlight = false;
let publicCheckAttempts = 0;
let publicCheckRetryTimer = null;

function withTimeout(promise) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('timeout')), FETCH_MS);
    }),
  ]);
}

function parseIsoAsUtcMs(raw) {
  if (typeof raw !== 'string') {
    return NaN;
  }
  const normalized = raw.trim().replace(' ', 'T');
  const hasTimezoneSuffix = /([zZ]|[+-]\d{2}:?\d{2})$/.test(normalized);
  const candidate = hasTimezoneSuffix ? normalized : `${normalized}Z`;
  const ms = Date.parse(candidate);
  return Number.isNaN(ms) ? NaN : ms;
}

function parseUtcMsFromJson(data) {
  if (!data || typeof data !== 'object') {
    return NaN;
  }
  if (typeof data.unix_ms === 'number' && Number.isFinite(data.unix_ms)) {
    return data.unix_ms;
  }
  if (typeof data.unixtime === 'number' && Number.isFinite(data.unixtime)) {
    return data.unixtime * 1000;
  }
  const iso = data.utc_datetime || data.dateTime || data.dateTimeUtc || data.dateTimeUTC;
  if (iso) {
    const ms = parseIsoAsUtcMs(iso);
    if (!Number.isNaN(ms)) {
      return ms;
    }
  }
  if (typeof data.year === 'number' && typeof data.month === 'number') {
    return Date.UTC(
      data.year,
      data.month - 1,
      data.day || 1,
      data.hour || 0,
      data.minute != null ? data.minute : 0,
      data.seconds != null ? data.seconds : 0,
      data.milliSeconds != null ? data.milliSeconds : 0,
    );
  }
  return NaN;
}

const PUBLIC_TIME_URLS = [
  'https://worldtimeapi.org/api/ip',
  'https://timeapi.io/api/Time/current/zone?timeZone=UTC',
  'https://timeapi.io/api/v1/time/current/utc',
];

function fetchOneJson(url) {
  return withTimeout(fetch(url, { cache: 'no-store' }))
    .then((r) => {
      if (!r.ok) {
        throw new Error('bad');
      }
      return r.json();
    })
    .then((data) => {
      const ms = parseUtcMsFromJson(data);
      if (Number.isNaN(ms)) {
        throw new Error('bad');
      }
      return ms;
    });
}

function fetchReferenceUtcMs() {
  return PUBLIC_TIME_URLS.reduce(
    (chain, url) => chain.catch(() => fetchOneJson(url)),
    Promise.reject(new Error('try-first-url')),
  );
}

function clearRetryTimer() {
  if (!publicCheckRetryTimer) {
    return;
  }
  clearTimeout(publicCheckRetryTimer);
  publicCheckRetryTimer = null;
}

/**
 * Reset trạng thái kiểm tra (gọi khi logout).
 * Xóa sessionStorage + reset biến module để lần đăng nhập sau được kiểm tra lại.
 */
export function resetUserClockSkewCheck() {
  sessionStorage.removeItem(SESSION_WARNED_KEY);
  clearRetryTimer();
  publicCheckFinished = false;
  publicCheckInFlight = false;
  publicCheckAttempts = 0;
}

function showWarningIfSkew(refMs) {
  if (sessionStorage.getItem(SESSION_WARNED_KEY)) {
    return;
  }
  const skew = Math.abs(refMs - Date.now());
  if (skew <= CLOCK_SKEW_WARN_THRESHOLD_MS) {
    return;
  }
  sessionStorage.setItem(SESSION_WARNED_KEY, '1');
  clearRetryTimer();
  publicCheckFinished = true;
  const toastConfig = {
    ...ToastUtils.getToastConfig('warning'),
    duration: null,
    action: {
      text: Vue.i18n.t('app.understood'),
      onClick: (e, toastObject) => {
        toastObject.goAway(0);
      },
    },
  };
  Vue.toasted.show(Vue.i18n.t('app.clockSkewWarning'), toastConfig);
}

function applyPublicReference(refMs) {
  if (publicCheckFinished || sessionStorage.getItem(SESSION_WARNED_KEY)) {
    return;
  }
  publicCheckFinished = true;
  clearRetryTimer();
  showWarningIfSkew(refMs);
}

function runPublicClockCheck() {
  if (publicCheckFinished || sessionStorage.getItem(SESSION_WARNED_KEY)) {
    return;
  }
  if (publicCheckInFlight) {
    return;
  }
  if (publicCheckAttempts >= MAX_PUBLIC_CHECK_ATTEMPTS) {
    return;
  }
  publicCheckInFlight = true;
  publicCheckAttempts += 1;

  fetchReferenceUtcMs()
    .then((refMs) => {
      applyPublicReference(refMs);
    })
    .catch(() => {
      if (
        publicCheckFinished
        || sessionStorage.getItem(SESSION_WARNED_KEY)
        || publicCheckAttempts >= MAX_PUBLIC_CHECK_ATTEMPTS
        || publicCheckRetryTimer
      ) {
        return;
      }
      publicCheckRetryTimer = setTimeout(() => {
        publicCheckRetryTimer = null;
        runPublicClockCheck();
      }, PUBLIC_CHECK_RETRY_MS);
    })
    .finally(() => {
      publicCheckInFlight = false;
    });
}

/**
 * Interceptor gọi hàm này khi có response API để kích hoạt kiểm tra lại.
 * Không kết luận từ header Date nhằm tránh false warning do clock lệch phía server.
 */
export function trySkewFromHttpHeaders() {
  runPublicClockCheck();
}

export function checkUserClockSkewOnce() {
  if (sessionStorage.getItem(SESSION_WARNED_KEY) || publicCheckFinished) {
    return;
  }
  runPublicClockCheck();
}

/**
 * Ngay sau dang nhap thanh cong: goi reset roi kiem tra gio (sau Vue.nextTick).
 */
export function runClockSkewCheckForNewLoginSession() {
  resetUserClockSkewCheck();
  Vue.nextTick(() => {
    checkUserClockSkewOnce();
  });
}



// WEBPACK FOOTER //
// ./src/utils/clock-skew.js